import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Headphones, Upload, X, Play, Eye, EyeOff, Trash2 } from 'lucide-react';

export function AdminAudiobooksTab({
  audiobooksMerchant, audiobooksInfluencer, uploadingAB,
  handleUploadAudiobook, handleDeleteAudiobook,
  tutorialVideo, uploadingTutorial,
  handleUploadTutorial, handleToggleTutorial, handleDeleteTutorial
}) {
  return (
    <div className="space-y-6">
      {/* Tutorial Video Section */}
      <Card className="bg-white border-2 border-amber-200" data-testid="tutorial-video-admin">
        <CardHeader>
          <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
            <Play className="h-5 w-5 text-amber-500" /> Video Tutorial — "Como Usar" (Landing Page)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tutorialVideo ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                <div>
                  <p className="font-medium text-slate-900">{tutorialVideo.original_name || tutorialVideo.filename}</p>
                  <p className="text-xs text-slate-400">
                    Enviado em {new Date(tutorialVideo.uploaded_at).toLocaleDateString('pt-BR')}
                    {' — '}
                    {tutorialVideo.size_bytes ? `${(tutorialVideo.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ''}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {tutorialVideo.visible ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        <Eye className="h-3 w-3" /> Visivel na Landing
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">
                        <EyeOff className="h-3 w-3" /> Oculto
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={handleToggleTutorial}
                    className={tutorialVideo.visible ? 'border-slate-300 text-slate-600' : 'border-green-300 text-green-600'}
                    data-testid="tutorial-toggle-btn">
                    {tutorialVideo.visible ? <><EyeOff className="h-4 w-4 mr-1" /> Ocultar</> : <><Eye className="h-4 w-4 mr-1" /> Exibir</>}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleDeleteTutorial}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid="tutorial-delete-btn">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {tutorialVideo.file_url && (
                <video controls className="w-full max-w-lg rounded-lg border" data-testid="tutorial-preview-player">
                  <source src={tutorialVideo.file_url} type="video/mp4" />
                </video>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-slate-400 mb-4">Nenhum video tutorial cadastrado</p>
              <label className="cursor-pointer">
                <input type="file" accept=".mp4" className="hidden" onChange={handleUploadTutorial} disabled={uploadingTutorial} />
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-lg cursor-pointer transition-all"
                  data-testid="tutorial-upload-btn">
                  <Upload className="h-4 w-4" /> {uploadingTutorial ? 'Enviando...' : 'Upload Video Tutorial (.mp4)'}
                </span>
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Existing AudioBooks sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200" data-testid="audiobooks-merchant-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-slate-900 flex items-center gap-2"><Headphones className="h-5 w-5 text-blue-500" /> AudioBooks para Lojistas</CardTitle>
              <label className="cursor-pointer">
                <input type="file" accept=".mp4" className="hidden" onChange={e => handleUploadAudiobook(e, 'merchant')} disabled={uploadingAB} />
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg cursor-pointer">
                  <Upload className="h-3 w-3" /> {uploadingAB ? 'Enviando...' : 'Upload MP4'}
                </span>
              </label>
            </div>
          </CardHeader>
          <CardContent>
            {audiobooksMerchant.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Nenhum audiobook para lojistas</p>
            ) : (
              <div className="space-y-3">
                {audiobooksMerchant.map(ab => (
                  <div key={ab.audiobook_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div><p className="font-medium text-slate-900">{ab.title}</p><p className="text-xs text-slate-400">{new Date(ab.uploaded_at).toLocaleDateString('pt-BR')}</p></div>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteAudiobook(ab.audiobook_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-ab-${ab.audiobook_id}`}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200" data-testid="audiobooks-influencer-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-slate-900 flex items-center gap-2"><Headphones className="h-5 w-5 text-indigo-500" /> AudioBooks para Influenciadores</CardTitle>
              <label className="cursor-pointer">
                <input type="file" accept=".mp4" className="hidden" onChange={e => handleUploadAudiobook(e, 'influencer')} disabled={uploadingAB} />
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg cursor-pointer">
                  <Upload className="h-3 w-3" /> {uploadingAB ? 'Enviando...' : 'Upload MP4'}
                </span>
              </label>
            </div>
          </CardHeader>
          <CardContent>
            {audiobooksInfluencer.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">Nenhum audiobook para influenciadores</p>
            ) : (
              <div className="space-y-3">
                {audiobooksInfluencer.map(ab => (
                  <div key={ab.audiobook_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div><p className="font-medium text-slate-900">{ab.title}</p><p className="text-xs text-slate-400">{new Date(ab.uploaded_at).toLocaleDateString('pt-BR')}</p></div>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteAudiobook(ab.audiobook_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
